/**
 * Shared TTL preset constants for every share-grant surface — `SharePopover`
 * (mint/regenerate) AND `EditShareGrantDialog` (Links tab + Task 15's gear
 * icon). Extracted here (Task 11) so both surfaces render the identical
 * three presets with identical labels instead of two hand-copied lists that
 * could drift.
 */
import type { ShareGrantDto, ShareGrantTtl } from '@discovr/features';

export const TTL_OPTIONS: { value: ShareGrantTtl; label: string }[] = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

/**
 * TTL a fresh share dialog opens on. The shortest preset is deliberate: a
 * public link is the riskiest of the three by default, so an admin who
 * doesn't think about expiry gets the least exposure and has to opt IN to a
 * longer life. (The backend's own DefaultShareTTL is separate — it only
 * applies to a mint request that omits ttl, which this UI never sends.)
 */
export const DEFAULT_SHARE_TTL: ShareGrantTtl = '24h';

// Human phrasing shared by the TTL toast and the live status line — keep the
// two in sync so "Expiry set to 30 days" and "expires 30 days" always agree.
export const TTL_HUMAN: Record<ShareGrantTtl, string> = {
  '24h': '24 hours',
  '7d': '7 days',
  '30d': '30 days',
};

const TTL_MS: Record<ShareGrantTtl, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
};

/**
 * Approximates which TTL preset a grant was originally minted (or last
 * edited) with, from its created_at/expires_at timestamps — ShareGrantDto
 * carries no explicit ttl enum, only those two dates. Picks whichever
 * preset's duration is closest to the grant's actual lifespan. Used by
 * "Regenerate password" (SharePopover) and to pre-select the TTL segmented
 * control's initial value (EditShareGrantDialog) so neither silently resets
 * a short-lived grant back to the 30-day default.
 */
export function closestTtlPreset(grant: ShareGrantDto): ShareGrantTtl {
  const durationMs = new Date(grant.expires_at).getTime() - new Date(grant.created_at).getTime();
  if (!Number.isFinite(durationMs) || durationMs <= 0) return '30d';
  return TTL_OPTIONS.reduce<{ ttl: ShareGrantTtl; diff: number }>(
    (closest, opt) => {
      const diff = Math.abs(TTL_MS[opt.value] - durationMs);
      return diff < closest.diff ? { ttl: opt.value, diff } : closest;
    },
    { ttl: '30d', diff: Infinity },
  ).ttl;
}
